import { useGetList } from "react-admin";
import { RefCodeType } from "./useRefCodeSearch";

/**
 * Result of a single-code lookup against `/ref-codes/{codeType}`.
 *
 * - `description` is `null` when the code is missing, unknown, or the lookup
 *   errored. Callers should render nothing in that case.
 * - `loading` is true only while a real query is in flight. When `code` is
 *   empty or falsy, the lookup is disabled entirely and `loading` stays false.
 */
export interface UseRefCodeLookupResult {
  description: string | null;
  loading: boolean;
}

/** Per-codeType field name on the API response that carries the human-readable description. */
const descriptionField = (codeType: RefCodeType): string => {
  switch (codeType) {
    case "cpt":
      return "short_description";
    case "ndc":
      return "product_name";
    case "icd":
    default:
      return "description";
  }
};

/**
 * Fetches the description for a single known code. Used by read-only claim
 * item views to enrich raw codes with their human-readable meanings.
 *
 * Non-blocking by design — errors and cache misses both return
 * `{ description: null }` so the caller can simply not render the hint.
 *
 * @example
 *   const { description } = useRefCodeLookup("icd", "E11.65");
 *   // -> "Type 2 diabetes mellitus with hyperglycemia"
 */
export const useRefCodeLookup = (codeType: RefCodeType, code: string | undefined | null): UseRefCodeLookupResult => {
  const trimmed = code?.trim() ?? "";
  const enabled = trimmed.length > 0;
  const descKey = descriptionField(codeType);

  const { data, isLoading, error } = useGetList(
    `ref-codes/${codeType}`,
    {
      filter: { q: trimmed },
      pagination: { page: 1, perPage: 1 },
      sort: { field: "relevance", order: "DESC" },
    },
    { enabled }
  );

  if (!enabled || error || !data || data.length === 0) {
    return { description: null, loading: false };
  }

  // The API returns best-relevance first. Only accept an exact code match —
  // a fuzzy hit on a different code would be misleading as enrichment.
  const top = data[0] as Record<string, unknown>;
  if (String(top.code ?? "") !== trimmed) {
    return { description: null, loading: enabled && isLoading };
  }

  return {
    description: String(top[descKey] ?? "") || null,
    loading: false,
  };
};
